import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faTrash,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";

const SeriesMasterList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const sewadarId = localStorage.getItem("sewadarId");
  const navigate = useNavigate();

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${SERVER_BASE}/series`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch series list", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Series?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
     const res= await apiClient.delete(`${SERVER_BASE}/delete-series/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Swal.fire({
              icon: "success",
              text: res?.data?.message,
              toast: true,
              position: "top-end",
              timer: 1000,
              showConfirmButton: false,
              timerProgressBar:true
            }).then(()=>{

              fetchSeries();
            });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "warning",
        text: err.response?.data?.message,
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
        timerProgressBar:true
      })
    }
  };

  /* ---------------- MARK COMPLETED ---------------- */
  const handleComplete = async (id) => {
    const result = await Swal.fire({
      icon: "question",
      title: "Mark as Completed?",
      text: "This will mark the series as Completed.",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    try {
      const res=await apiClient.put(
        `${SERVER_BASE}/series-complete/${id}`,
        { sewadarId }, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      Swal.fire({
        icon: "success",
        text: res?.data?.message,
        toast: true,
        position: "top-end",
        timer: 1000,
        showConfirmButton: false,
        timerProgressBar:true
      }).then(()=>{
        fetchSeries();
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  /* ---------------- TABLE COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        Header: "#",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Department",
        accessor: "departmentName",
      },
      {
        Header: "Starting No",
        accessor: "startingNo",
      },
      {
        Header: "Ending No",
        accessor: "endingNo",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Action",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            {/* ✅ HIDE EDIT WHEN COMPLETED */}
            {row.original.status != "Completed" && ( <>
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() =>
                  navigate("../SeriesMaster", {
                    state: { row: row.original },
                  })
                }
                title="Edit"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
          

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleComplete(row.original.id)}
              title="Mark Completed"
            >
              <FontAwesomeIcon icon={faCheckCircle} />
            </button>
            </>
  )}
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.original.id)}
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  /* ---------------- TABLE INSTANCE ---------------- */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 25 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  /* ---------------- UI ---------------- */
  return (
    <div className="formcontainer mt-5">
      <h4>Series Master</h4>

      {/* Top Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate("../SeriesMaster")}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Series
        </button>

        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search..."
          style={{ width: "60%" }}
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />

        <select
          className="form-select form-select-sm w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[25, 50, 100, 200].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <table
        className="table table-bordered table-striped"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td colSpan="6" className="text-center">
                Loading...
              </td>
            </tr>
          ) : page.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No records found
              </td>
            </tr>
          ) : (
            page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <button
          className="btn btn-sm btn-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of{" "}
          <strong>{pageOptions.length}</strong>
        </span>

        <button
          className="btn btn-sm btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SeriesMasterList;
